/**
 * Editor state: the working scenario, the current selection, and mutators,
 * plus a small section of UI state (panel open/width) that multiple editor
 * chrome components need to share.
 *
 * Zustand keeps everything loosely coupled. The canvas reads scenario +
 * selection + mutator dispatch; the panel reads the selected node + UI
 * state + mutators; the toolbar reads UI state + title; the (future) modals
 * will read the relevant slice. No prop drilling.
 *
 * Convention: mutators that touch the scenario carry the same name they had
 * in the prototype's main component (`updateNode`, `setStartNode`,
 * `deleteNode`) — so anyone porting from the prototype's logic recognises
 * the names immediately.
 */
import { create } from 'zustand';
import {
  blankScenario,
  migrateIfNeeded,
  validateScenario,
  newId,
  type Choice,
  type Condition,
  type Destination,
  type Effect,
  type FeedbackVariant,
  type Branding,
  type IntroScreen,
  type ClosingScreen,
  type DisplaySettings,
  type ObjectiveDecl,
  type PublishedScenario,
  type Scenario,
  type ScenarioNode,
  type SceneVariant,
  type ValidationResult,
  type VariableDecl,
} from '@branchworks/shared';
import { getRepository } from '../persistence';
import type { StoredScenario } from '../persistence/ScenarioRepository';

interface ScenarioStore {
  // ----- Scenario state -----

  /** The current working scenario. */
  scenario: Scenario;
  /** Which node is currently selected, or null. */
  selectedId: string | null;

  // ----- Persistence state (Phase 5a) -----

  /**
   * The id of the currently-open scenario in the repository, or null before
   * hydration completes. Distinct from anything inside the scenario JSON.
   */
  scenarioId: string | null;
  /**
   * Whether the initial load from the repository has completed. Until this is
   * true, autosave is suppressed — otherwise the default in-memory scenario
   * would clobber stored data before we've had a chance to load it.
   */
  hydrated: boolean;
  /** Autosave status, surfaced in the toolbar. */
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  /** ISO timestamp the current scenario was created (for the stored record). */
  createdAt: string;

  // ----- UI state (panel chrome) -----

  /** Whether the right detail panel is currently visible. */
  panelOpen: boolean;
  /** Current panel width in px, between PANEL_MIN and PANEL_MAX. */
  panelWidth: number;

  // ----- Scenario mutators -----

  /** Replace the entire scenario (used by import). Runs migration on input. */
  setScenario: (raw: unknown) => void;

  /** Edit the scenario's title. */
  setTitle: (title: string) => void;

  // ----- V6 scenario chrome: branding, title screen, end screen -----
  /** Merge a patch into `scenario.branding`; empty-string fields are pruned,
   *  and branding is removed entirely once it holds nothing. */
  updateBranding: (patch: Partial<Branding>) => void;
  /** Turn the title screen on (seeds `{ heading: '' }`) or off (removes `intro`). */
  setIntroEnabled: (enabled: boolean) => void;
  /** Merge a patch into `scenario.intro`. No-op if the title screen is off. */
  updateIntro: (patch: Partial<IntroScreen>) => void;
  /** Turn the end screen on (seeds `{ heading: '' }`) or off (removes `closing`). */
  setClosingEnabled: (enabled: boolean) => void;
  /** Merge a patch into `scenario.closing`. No-op if the end screen is off. */
  updateClosing: (patch: Partial<ClosingScreen>) => void;

  // ----- V7 output sizing: page size + text size -----
  /** Merge a patch into `scenario.display`. `pageSize: null` clears the fixed
   *  size (→ responsive); `textSize: null` clears it (→ default "medium").
   *  `display` is removed entirely once it holds nothing, so the default
   *  (responsive + medium) is byte-identical to an untouched scenario. */
  updateDisplay: (patch: {
    pageSize?: DisplaySettings['pageSize'] | null;
    textSize?: DisplaySettings['textSize'] | null;
  }) => void;

  /** Change selection. Pass null to clear. */
  selectNode: (id: string | null) => void;

  /** Move a node to a new canvas position. Used by React Flow drag-end. */
  moveNode: (id: string, x: number, y: number) => void;

  /**
   * Patch a node's top-level fields (title, type, outcomeLabel, etc.).
   * Variants, choices, entryEffects, and objectiveTags use their own
   * dedicated mutators.
   */
  updateNode: (id: string, patch: Partial<ScenarioNode>) => void;

  /**
   * Replace the entire variants chain for a node. Owned by `SceneVariantsEditor`.
   * The component guarantees the chain shape (at least one entry, last has
   * `condition: null`); the store just persists it. Supersedes the bare-mode
   * `updateFallbackVariant` from Phase 1b.2a now that the full chain editor exists.
   */
  replaceVariants: (nodeId: string, newVariants: SceneVariant[]) => void;

  /** Replace a node's entry effects. Owned by `EffectsEditor`. */
  replaceEntryEffects: (nodeId: string, newEffects: Effect[]) => void;

  /**
   * Patch a single choice's top-level fields (label, visibleIf, etc.).
   * Destination chain, effects, and feedback variants use their own mutators.
   */
  updateChoice: (nodeId: string, choiceIdx: number, patch: Partial<Choice>) => void;

  /**
   * Append a new choice to a node. No-op if the node already has 4 choices
   * (the prototype's cap). The new choice has an empty label, one unwired
   * unconditional destination, and one empty-text feedback variant — so it's
   * a valid V4 shape from the moment it's created.
   */
  addChoice: (nodeId: string) => void;

  /** Delete a choice from a node by index. */
  deleteChoice: (nodeId: string, choiceIdx: number) => void;

  /** Replace a choice's destinations chain. Owned by `DestinationChainEditor`. */
  replaceDestinations: (nodeId: string, choiceIdx: number, newDestinations: Destination[]) => void;

  /** Replace a choice's effects list. Owned by `EffectsEditor`. */
  replaceChoiceEffects: (nodeId: string, choiceIdx: number, newEffects: Effect[]) => void;

  /**
   * Replace a choice's feedback variants chain. Owned by `FeedbackVariantsEditor`.
   * The component guarantees the chain shape (at least one entry, last has
   * `condition: null`); the store just persists it.
   */
  replaceFeedbackVariants: (
    nodeId: string,
    choiceIdx: number,
    newVariants: FeedbackVariant[],
  ) => void;

  /**
   * Set the objective-tag ids on a node. Owned by `ObjectiveTagsEditor`.
   * Empty arrays are dropped from the underlying object so V4 export stays
   * tidy (same discipline as `entryEffects` and choice `effects`).
   */
  setNodeObjectiveTags: (nodeId: string, tagIds: string[]) => void;

  /**
   * Set the objective-tag ids on a choice. Owned by `ObjectiveTagsEditor`.
   * Empty arrays are dropped, same as the node mutator.
   */
  setChoiceObjectiveTags: (nodeId: string, choiceIdx: number, tagIds: string[]) => void;

  /** Promote a node to the scenario's entry point. */
  setStartNode: (id: string) => void;

  /**
   * Delete a node. Cleans up: any destination chain entry on any other
   * node whose `destinationId` was pointing at the deleted node gets its
   * destinationId nulled (the prototype's cascade behaviour). Clears
   * selection if the deleted node was selected. Refuses to delete the
   * start node.
   */
  deleteNode: (id: string) => void;

  /**
   * Create a new decision node and select it. Position is computed as an
   * offset from the most-recently-added node so multiple Adds in a row
   * don't stack on top of each other. Caller can override position
   * explicitly via the optional argument.
   */
  addNode: (position?: { x: number; y: number }) => string;

  /**
   * Persist a drag-to-connect gesture: the user dragged from
   * `(sourceNodeId, choiceId)` (the bottom-handle source) to `targetNodeId`
   * (the top-handle target). Always wires the choice's *fallback*
   * destination to the target node — that's the simple "drag = wire this
   * choice here" mental model that node editors everywhere use. Authors
   * who want conditional destinations add them via the chain editor in
   * the detail panel; the canvas is for plumbing the default routing.
   *
   * No-op if any of (sourceNode, choice, targetNode) doesn't exist.
   */
  connectChoice: (sourceNodeId: string, choiceId: string, targetNodeId: string) => void;

  // ----- Variable mutators (Phase 1b.3) -----

  /**
   * Declare a new variable. Returns the new variable's name on success or
   * an error message on failure (collision, invalid name).
   */
  addVariable: (name: string, decl: VariableDecl) => { ok: true } | { ok: false; error: string };

  /** Edit a variable's description, type, or default. Caller validates type/default coherence. */
  updateVariable: (name: string, patch: Partial<VariableDecl>) => void;

  /**
   * Rename a variable. Walks every condition (visibleIf, variant, destination,
   * feedback) and every effect (entry, choice), substituting the new name on
   * any comparison/effect referencing the old one. Returns `ok: false` if the
   * new name collides or is invalid.
   */
  renameVariable: (oldName: string, newName: string) => { ok: true } | { ok: false; error: string };

  /**
   * Delete a variable. Cascades through everything: strips comparisons
   * referencing the deleted variable from every condition; removes
   * non-fallback chain entries whose condition becomes empty (preserves
   * fallbacks by setting their condition to null); strips effects.
   */
  deleteVariable: (name: string) => void;

  // ----- Objective mutators (Phase 1b.3) -----

  /**
   * Declare a new objective. The id is generated; the human-facing `code`
   * is part of the decl. Returns the new id on success.
   */
  addObjective: (decl: ObjectiveDecl) => { ok: true; id: string } | { ok: false; error: string };

  /** Edit an objective's code, description, or Bloom's level. */
  updateObjective: (id: string, patch: Partial<ObjectiveDecl>) => void;

  /**
   * Delete an objective. Cascades through every node and every choice's
   * `objectiveTags`, stripping the deleted id. Empty arrays drop to undefined
   * (drop-on-empty discipline matches the rest of the store).
   */
  deleteObjective: (id: string) => void;

  // ----- UI mutators -----

  /** Show/hide the right detail panel. */
  setPanelOpen: (open: boolean) => void;
  /** Set panel width in px; clamped to [PANEL_MIN, PANEL_MAX]. */
  setPanelWidth: (width: number) => void;

  /**
   * Which modal is currently open, if any. Only one modal is open at a time —
   * a stack would suggest features that aren't built (escape-key dismissal,
   * z-ordering, etc.) and the single-active design matches the prototype.
   */
  openModal: ModalKind;
  /** Open a modal (or close all if `null`). */
  setOpenModal: (kind: ModalKind) => void;

  // ----- Published-version mutators (Phase 4c) -----

  /**
   * In-memory list of published versions, ordered most-recent-first. Each is
   * an immutable snapshot of the draft at the moment of publish.
   *
   * No persistence yet; refresh and the list is gone. Phase 5 (Supabase) makes
   * these durable. Authors who want a permanent copy can Export the version's
   * JSON via the Versions modal.
   */
  publishedVersions: PublishedScenario[];

  /**
   * Snapshot the current draft into a new published version. Returns the new
   * version's tag on success, or an error message on failure (duplicate tag,
   * validation errors). Validation errors block publish — warnings don't.
   */
  publishDraft: (
    versionTag: string,
  ) => { ok: true; versionTag: string } | { ok: false; error: string };

  /**
   * Load a published version back into the editor as the working draft. Does
   * NOT delete the published version — restoration is a "clone and edit"
   * gesture. Refuses if there are unsaved changes that haven't been published
   * (with a `force` override flag for the second confirmation).
   */
  restoreFromPublished: (versionTag: string) => boolean;

  /**
   * Delete a published version from the in-memory list. Used by the Versions
   * modal's Remove action; doesn't affect the current draft.
   */
  deletePublishedVersion: (versionTag: string) => void;

  // ----- Persistence mutators (Phase 5a) -----

  /**
   * Hydrate from the repository on app startup. Loads the scenario list; if a
   * scenario exists, opens the most-recently-updated one; if the library is
   * empty, seeds the canonical example as the first scenario. Sets `hydrated`.
   * Idempotent — safe to call once from an effect.
   */
  hydrate: () => Promise<void>;

  /** Persist the current scenario + published versions to the repository now. */
  saveNow: () => Promise<void>;

  /** Open a scenario from the library by id. */
  openScenario: (id: string) => Promise<void>;

  /** Create a fresh scenario (seeded from the canonical example), open it. */
  createScenario: () => Promise<void>;

  /** Duplicate an existing scenario by id (deep clone, new id, "(copy)" title). */
  duplicateScenario: (id: string) => Promise<void>;

  /** Delete a scenario from the library. If it's the open one, opens another (or seeds). */
  deleteScenario: (id: string) => Promise<void>;

  /** Set save status (used by the autosave effect). */
  setSaveStatus: (status: 'idle' | 'saving' | 'saved' | 'error') => void;

  // ----- Selectors -----

  /** Current validation result. */
  getValidation: () => ValidationResult;
}

/** The set of modals the editor can open. */
export type ModalKind = 'variables' | 'objectives' | 'problems' | 'versions' | 'library' | 'settings' | null;

export const PANEL_MIN = 280;
export const PANEL_MAX = 600;
export const PANEL_DEFAULT = 380;

/**
 * Helper for adding a choice. Creates a V4-shaped default choice: empty
 * label, one unwired unconditional destination, one empty-text feedback
 * variant. The new choice is valid from the moment it's created — no
 * post-creation cleanup needed.
 */
function makeDefaultChoice(): Choice {
  return {
    id: newId('c'),
    label: '',
    destinations: [{ condition: null, destinationId: null }],
    feedbackVariants: [{ id: newId('vf'), condition: null, text: '' }],
  };
}

/**
 * Variable cascade helper. Walks every condition in the scenario applying
 * `transformComparison` to each comparison, and every effect applying
 * `transformEffect`. Returns either:
 *   - the transformed value (substitution), OR
 *   - `null` to drop the item (used by delete)
 *
 * For non-fallback chain entries (destination, variant, feedback) whose
 * condition ends up with zero comparisons, the entry is removed entirely
 * — that's the "remove on clear" gesture made consistent across the
 * cascade. The fallback entry's condition is forced to null if it somehow
 * isn't already.
 *
 * For visibleIf (single condition, not a chain): an empty condition becomes
 * undefined (always visible).
 *
 * For effects: filtered list, drop empty.
 */
function transformScenarioForVariableCascade(
  scenario: Scenario,
  transformComparison: (cmp: { variable: string; op: string; value: unknown }) =>
    | { variable: string; op: string; value: unknown }
    | null,
  transformEffect: (eff: { variable: string; op: string; value: unknown }) =>
    | { variable: string; op: string; value: unknown }
    | null,
): Scenario {
  const transformCondition = (cond: Condition | null | undefined): Condition | null => {
    if (!cond) return null;
    const next: typeof cond.comparisons = [];
    for (const c of cond.comparisons || []) {
      const result = transformComparison(c);
      if (result !== null) next.push(result as never);
    }
    if (next.length === 0) return null;
    return { ...cond, comparisons: next };
  };

  const transformEffectList = (effs: Effect[] | undefined): Effect[] => {
    if (!effs) return [];
    const out: Effect[] = [];
    for (const e of effs) {
      const result = transformEffect(e);
      if (result !== null) out.push(result as Effect);
    }
    return out;
  };

  const nodes: Record<string, ScenarioNode> = {};
  for (const [id, n] of Object.entries(scenario.nodes)) {
    // Entry effects: filter; drop the field if empty.
    const newEntryEffects = transformEffectList(n.entryEffects);
    const nextNode: ScenarioNode = { ...n };
    if (newEntryEffects.length === 0) {
      delete nextNode.entryEffects;
    } else {
      nextNode.entryEffects = newEntryEffects;
    }

    // Variants chain. Non-fallback entries with empty conditions get dropped;
    // fallback survives with condition forced to null.
    const variants = (n.variants || []).slice();
    const lastVariantIdx = variants.length - 1;
    const newVariants: SceneVariant[] = [];
    for (let i = 0; i < variants.length; i++) {
      const v = variants[i];
      const isFallback = i === lastVariantIdx;
      const newCondition = transformCondition(v.condition);
      if (!isFallback && newCondition === null) continue; // drop empty conditional
      newVariants.push({ ...v, condition: isFallback ? null : newCondition });
    }
    nextNode.variants = newVariants;

    // Choices
    nextNode.choices = n.choices.map((c) => {
      const newVisibleIf = transformCondition(c.visibleIf);
      const newEffects = transformEffectList(c.effects);

      // Destinations chain
      const dests = (c.destinations || []).slice();
      const lastDestIdx = dests.length - 1;
      const newDests: Destination[] = [];
      for (let i = 0; i < dests.length; i++) {
        const d = dests[i];
        const isFallback = i === lastDestIdx;
        const newCondition = transformCondition(d.condition);
        if (!isFallback && newCondition === null) continue;
        newDests.push({ ...d, condition: isFallback ? null : newCondition });
      }

      // Feedback chain — same rules.
      const feedbacks = (c.feedbackVariants || []).slice();
      const lastFbIdx = feedbacks.length - 1;
      const newFeedbacks: FeedbackVariant[] = [];
      for (let i = 0; i < feedbacks.length; i++) {
        const f = feedbacks[i];
        const isFallback = i === lastFbIdx;
        const newCondition = transformCondition(f.condition);
        if (!isFallback && newCondition === null) continue;
        newFeedbacks.push({ ...f, condition: isFallback ? null : newCondition });
      }

      const next: Choice = {
        ...c,
        destinations: newDests,
        feedbackVariants: newFeedbacks,
      };
      if (newVisibleIf) next.visibleIf = newVisibleIf;
      else delete next.visibleIf;
      if (newEffects.length > 0) next.effects = newEffects;
      else delete next.effects;
      return next;
    });

    nodes[id] = nextNode;
  }

  return { ...scenario, nodes };
}

/**
 * Objective cascade helper. Strips a given objective id from every
 * `objectiveTags` array on nodes and choices; drops empty arrays.
 */
function transformScenarioForObjectiveDelete(
  scenario: Scenario,
  deletedId: string,
): Scenario {
  const nodes: Record<string, ScenarioNode> = {};
  for (const [id, n] of Object.entries(scenario.nodes)) {
    const nextNode: ScenarioNode = { ...n };
    if (n.objectiveTags) {
      const filtered = n.objectiveTags.filter((t) => t !== deletedId);
      if (filtered.length === 0) delete nextNode.objectiveTags;
      else nextNode.objectiveTags = filtered;
    }
    nextNode.choices = n.choices.map((c) => {
      if (!c.objectiveTags) return c;
      const filtered = c.objectiveTags.filter((t) => t !== deletedId);
      const next: Choice = { ...c };
      if (filtered.length === 0) delete next.objectiveTags;
      else next.objectiveTags = filtered;
      return next;
    });
    nodes[id] = nextNode;
  }
  return { ...scenario, nodes };
}

/** Identifier validation: matches the prototype's regex. */
const VAR_NAME_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

/**
 * Synchronous guard against the hydrate() race. React Strict Mode invokes the
 * mount effect twice; both calls can observe `hydrated: false` before either
 * finishes the async load + `set({ hydrated: true })`, which would seed two
 * records. This flag flips synchronously on the first call so the second bails
 * immediately. Module-level because the store is a singleton.
 */
let hydrationInFlight = false;

export const useScenarioStore = create<ScenarioStore>((set, get) => ({
  scenario: blankScenario,
  selectedId: blankScenario.startNodeId,
  panelOpen: true,
  panelWidth: PANEL_DEFAULT,
  openModal: null,
  publishedVersions: [],
  scenarioId: null,
  hydrated: false,
  saveStatus: 'idle',
  createdAt: new Date().toISOString(),

  setScenario: (raw) => {
    try {
      const migrated = migrateIfNeeded(raw as never);
      set({ scenario: migrated, selectedId: migrated.startNodeId });
    } catch (e) {
      console.error('Branchworks: failed to import scenario', e);
    }
  },

  setTitle: (title) => set((s) => ({ scenario: { ...s.scenario, title } })),

  updateBranding: (patch) =>
    set((s) => {
      const merged: Branding = { ...(s.scenario.branding ?? {}), ...patch };
      // Prune empty-string fields so "cleared" really means absent.
      if (!merged.backgroundColor) delete merged.backgroundColor;
      if (!merged.logoUrl) delete merged.logoUrl;
      const branding = Object.keys(merged).length > 0 ? merged : undefined;
      return { scenario: { ...s.scenario, branding } };
    }),

  setIntroEnabled: (enabled) =>
    set((s) => ({
      scenario: {
        ...s.scenario,
        intro: enabled ? (s.scenario.intro ?? { heading: '' }) : undefined,
      },
    })),

  updateIntro: (patch) =>
    set((s) => {
      if (!s.scenario.intro) return s;
      return { scenario: { ...s.scenario, intro: { ...s.scenario.intro, ...patch } } };
    }),

  setClosingEnabled: (enabled) =>
    set((s) => ({
      scenario: {
        ...s.scenario,
        closing: enabled ? (s.scenario.closing ?? { heading: '' }) : undefined,
      },
    })),

  updateClosing: (patch) =>
    set((s) => {
      if (!s.scenario.closing) return s;
      return { scenario: { ...s.scenario, closing: { ...s.scenario.closing, ...patch } } };
    }),

  updateDisplay: (patch) =>
    set((s) => {
      const merged: DisplaySettings = { ...(s.scenario.display ?? {}) };
      // `null` in the patch means "clear this field"; undefined means "leave it".
      if ('pageSize' in patch) {
        if (patch.pageSize) merged.pageSize = patch.pageSize;
        else delete merged.pageSize;
      }
      if ('textSize' in patch) {
        if (patch.textSize) merged.textSize = patch.textSize;
        else delete merged.textSize;
      }
      // Prune to undefined so responsive + medium == an untouched scenario.
      const display = Object.keys(merged).length > 0 ? merged : undefined;
      return { scenario: { ...s.scenario, display } };
    }),

  selectNode: (id) => set({ selectedId: id }),

  moveNode: (id, x, y) => {
    set((state) => {
      const node = state.scenario.nodes[id];
      if (!node) return state;
      return {
        scenario: {
          ...state.scenario,
          nodes: {
            ...state.scenario.nodes,
            [id]: { ...node, x: Math.round(x), y: Math.round(y) },
          },
        },
      };
    });
  },

  updateNode: (id, patch) => {
    set((state) => {
      const node = state.scenario.nodes[id];
      if (!node) return state;
      return {
        scenario: {
          ...state.scenario,
          nodes: { ...state.scenario.nodes, [id]: { ...node, ...patch } },
        },
      };
    });
  },

  replaceVariants: (nodeId, newVariants) => {
    set((state) => {
      const node = state.scenario.nodes[nodeId];
      if (!node) return state;
      return {
        scenario: {
          ...state.scenario,
          nodes: { ...state.scenario.nodes, [nodeId]: { ...node, variants: newVariants } },
        },
      };
    });
  },

  replaceEntryEffects: (nodeId, newEffects) => {
    set((state) => {
      const node = state.scenario.nodes[nodeId];
      if (!node) return state;
      // Drop the field entirely when there are no effects, so V4 scenarios
      // round-trip cleanly through export without empty arrays in places
      // they were never authored.
      const next: ScenarioNode = newEffects.length === 0
        ? (() => {
            const { entryEffects: _drop, ...rest } = node;
            return rest as ScenarioNode;
          })()
        : { ...node, entryEffects: newEffects };
      return {
        scenario: {
          ...state.scenario,
          nodes: { ...state.scenario.nodes, [nodeId]: next },
        },
      };
    });
  },

  updateChoice: (nodeId, choiceIdx, patch) => {
    set((state) => {
      const node = state.scenario.nodes[nodeId];
      if (!node || !node.choices[choiceIdx]) return state;
      const choices = node.choices.map((c, i) => (i === choiceIdx ? { ...c, ...patch } : c));
      return {
        scenario: {
          ...state.scenario,
          nodes: { ...state.scenario.nodes, [nodeId]: { ...node, choices } },
        },
      };
    });
  },

  addChoice: (nodeId) => {
    set((state) => {
      const node = state.scenario.nodes[nodeId];
      if (!node) return state;
      if (node.choices.length >= 4) return state; // prototype cap
      const choices = [...node.choices, makeDefaultChoice()];
      return {
        scenario: {
          ...state.scenario,
          nodes: { ...state.scenario.nodes, [nodeId]: { ...node, choices } },
        },
      };
    });
  },

  deleteChoice: (nodeId, choiceIdx) => {
    set((state) => {
      const node = state.scenario.nodes[nodeId];
      if (!node) return state;
      const choices = node.choices.filter((_, i) => i !== choiceIdx);
      return {
        scenario: {
          ...state.scenario,
          nodes: { ...state.scenario.nodes, [nodeId]: { ...node, choices } },
        },
      };
    });
  },

  replaceDestinations: (nodeId, choiceIdx, newDestinations) => {
    set((state) => {
      const node = state.scenario.nodes[nodeId];
      if (!node || !node.choices[choiceIdx]) return state;
      const choices = node.choices.map((c, i) =>
        i === choiceIdx ? { ...c, destinations: newDestinations } : c,
      );
      return {
        scenario: {
          ...state.scenario,
          nodes: { ...state.scenario.nodes, [nodeId]: { ...node, choices } },
        },
      };
    });
  },

  replaceChoiceEffects: (nodeId, choiceIdx, newEffects) => {
    set((state) => {
      const node = state.scenario.nodes[nodeId];
      if (!node || !node.choices[choiceIdx]) return state;
      const choices = node.choices.map((c, i) => {
        if (i !== choiceIdx) return c;
        // Same drop-on-empty discipline as entry effects.
        if (newEffects.length === 0) {
          const { effects: _drop, ...rest } = c;
          return rest as Choice;
        }
        return { ...c, effects: newEffects };
      });
      return {
        scenario: {
          ...state.scenario,
          nodes: { ...state.scenario.nodes, [nodeId]: { ...node, choices } },
        },
      };
    });
  },

  replaceFeedbackVariants: (nodeId, choiceIdx, newVariants) => {
    set((state) => {
      const node = state.scenario.nodes[nodeId];
      if (!node || !node.choices[choiceIdx]) return state;
      const choices = node.choices.map((c, i) =>
        i === choiceIdx ? { ...c, feedbackVariants: newVariants } : c,
      );
      return {
        scenario: {
          ...state.scenario,
          nodes: { ...state.scenario.nodes, [nodeId]: { ...node, choices } },
        },
      };
    });
  },

  setNodeObjectiveTags: (nodeId, tagIds) => {
    set((state) => {
      const node = state.scenario.nodes[nodeId];
      if (!node) return state;
      // Drop the field entirely when empty so V4 round-trips through export
      // cleanly. Same discipline as entryEffects / choice effects.
      const next: ScenarioNode =
        tagIds.length === 0
          ? (() => {
              const { objectiveTags: _drop, ...rest } = node;
              return rest as ScenarioNode;
            })()
          : { ...node, objectiveTags: tagIds };
      return {
        scenario: {
          ...state.scenario,
          nodes: { ...state.scenario.nodes, [nodeId]: next },
        },
      };
    });
  },

  setChoiceObjectiveTags: (nodeId, choiceIdx, tagIds) => {
    set((state) => {
      const node = state.scenario.nodes[nodeId];
      if (!node || !node.choices[choiceIdx]) return state;
      const choices = node.choices.map((c, i) => {
        if (i !== choiceIdx) return c;
        if (tagIds.length === 0) {
          const { objectiveTags: _drop, ...rest } = c;
          return rest as Choice;
        }
        return { ...c, objectiveTags: tagIds };
      });
      return {
        scenario: {
          ...state.scenario,
          nodes: { ...state.scenario.nodes, [nodeId]: { ...node, choices } },
        },
      };
    });
  },

  setStartNode: (id) => {
    set((state) => {
      if (!state.scenario.nodes[id]) return state;
      return { scenario: { ...state.scenario, startNodeId: id } };
    });
  },

  deleteNode: (id) => {
    set((state) => {
      if (id === state.scenario.startNodeId) return state; // refuse — start is protected
      if (!state.scenario.nodes[id]) return state;
      const { [id]: _removed, ...rest } = state.scenario.nodes;
      // Cascade: null out any destination pointing at the deleted node, so
      // those edges disappear cleanly rather than dangling. Same behaviour
      // as the prototype.
      const cleaned: typeof rest = {};
      for (const [k, n] of Object.entries(rest)) {
        cleaned[k] = {
          ...n,
          choices: n.choices.map((c) => ({
            ...c,
            destinations: (c.destinations || []).map((d) =>
              d.destinationId === id ? { ...d, destinationId: null } : d,
            ),
          })),
        };
      }
      return {
        scenario: { ...state.scenario, nodes: cleaned },
        selectedId: state.selectedId === id ? null : state.selectedId,
      };
    });
  },

  addNode: (position) => {
    const id = newId('n');
    set((state) => {
      // Position: caller-supplied, OR a small offset from the rightmost-bottommost
      // existing node so multiple Adds in a row don't stack. The fallback if
      // there are no nodes is (200, 200), but with a start node always present
      // this branch never fires in practice.
      let x = 200;
      let y = 200;
      if (position) {
        x = position.x;
        y = position.y;
      } else {
        const nodes = Object.values(state.scenario.nodes);
        if (nodes.length > 0) {
          // Right and slightly down from the rightmost node, so new nodes
          // appear in a predictable place.
          const rightmost = nodes.reduce((best, n) => (n.x > best.x ? n : best), nodes[0]);
          x = rightmost.x + 280;
          y = rightmost.y;
        }
      }
      const newNode: ScenarioNode = {
        id,
        title: 'New scene',
        type: 'decision',
        x: Math.round(x),
        y: Math.round(y),
        variants: [{ id: newId('v'), condition: null, sceneText: '', media: [] }],
        choices: [],
      };
      return {
        scenario: {
          ...state.scenario,
          nodes: { ...state.scenario.nodes, [id]: newNode },
        },
        selectedId: id,
      };
    });
    return id;
  },

  connectChoice: (sourceNodeId, choiceId, targetNodeId) => {
    set((state) => {
      const sourceNode = state.scenario.nodes[sourceNodeId];
      const targetNode = state.scenario.nodes[targetNodeId];
      if (!sourceNode || !targetNode) return state;
      const choiceIdx = sourceNode.choices.findIndex((c) => c.id === choiceId);
      if (choiceIdx < 0) return state;
      // Replace the fallback destination (the LAST entry in the chain by invariant).
      const choice = sourceNode.choices[choiceIdx];
      const destinations = (choice.destinations || []).slice();
      if (destinations.length === 0) {
        destinations.push({ condition: null, destinationId: targetNodeId });
      } else {
        const lastIdx = destinations.length - 1;
        destinations[lastIdx] = { condition: null, destinationId: targetNodeId };
      }
      const choices = sourceNode.choices.map((c, i) =>
        i === choiceIdx ? { ...c, destinations } : c,
      );
      return {
        scenario: {
          ...state.scenario,
          nodes: {
            ...state.scenario.nodes,
            [sourceNodeId]: { ...sourceNode, choices },
          },
        },
      };
    });
  },

  // ----- Variable mutators -----

  addVariable: (name, decl) => {
    const trimmed = name.trim();
    if (!trimmed) return { ok: false, error: 'Name required' };
    if (!VAR_NAME_RE.test(trimmed)) {
      return { ok: false, error: 'Use letters, digits, underscores; must not start with a digit.' };
    }
    const existing = get().scenario.variables;
    if (existing[trimmed]) return { ok: false, error: `A variable named "${trimmed}" already exists.` };
    set((state) => ({
      scenario: {
        ...state.scenario,
        variables: { ...state.scenario.variables, [trimmed]: decl },
      },
    }));
    return { ok: true };
  },

  updateVariable: (name, patch) => {
    set((state) => {
      const cur = state.scenario.variables[name];
      if (!cur) return state;
      return {
        scenario: {
          ...state.scenario,
          variables: { ...state.scenario.variables, [name]: { ...cur, ...patch } },
        },
      };
    });
  },

  renameVariable: (oldName, newName) => {
    const trimmed = newName.trim();
    if (oldName === trimmed) return { ok: true };
    if (!trimmed) return { ok: false, error: 'Name required' };
    if (!VAR_NAME_RE.test(trimmed)) {
      return { ok: false, error: 'Use letters, digits, underscores; must not start with a digit.' };
    }
    const existing = get().scenario.variables;
    if (!existing[oldName]) return { ok: false, error: `Variable "${oldName}" doesn't exist.` };
    if (existing[trimmed]) return { ok: false, error: `A variable named "${trimmed}" already exists.` };

    set((state) => {
      const { [oldName]: decl, ...rest } = state.scenario.variables;
      const variables = { ...rest, [trimmed]: decl };
      // Cascade: walk every condition and effect, rename oldName → trimmed.
      const scenario = transformScenarioForVariableCascade(
        { ...state.scenario, variables },
        (cmp) => (cmp.variable === oldName ? { ...cmp, variable: trimmed } : cmp),
        (eff) => (eff.variable === oldName ? { ...eff, variable: trimmed } : eff),
      );
      return { scenario };
    });
    return { ok: true };
  },

  deleteVariable: (name) => {
    set((state) => {
      if (!state.scenario.variables[name]) return state;
      const { [name]: _drop, ...rest } = state.scenario.variables;
      // Cascade: strip every reference. Comparison and effect transforms
      // return `null` for items referencing the deleted variable; the cascade
      // walker drops them. Chain entries whose conditions become empty get
      // removed (non-fallback only).
      const scenario = transformScenarioForVariableCascade(
        { ...state.scenario, variables: rest },
        (cmp) => (cmp.variable === name ? null : cmp),
        (eff) => (eff.variable === name ? null : eff),
      );
      return { scenario };
    });
  },

  // ----- Objective mutators -----

  addObjective: (decl) => {
    if (!decl.code || !decl.code.trim()) {
      return { ok: false, error: 'Code required.' };
    }
    const trimmedCode = decl.code.trim();
    // Code uniqueness is a nicety, not strictly required (id is the key).
    // The prototype enforces it; we match.
    const existing = Object.values(get().scenario.objectives);
    if (existing.some((o) => o.code === trimmedCode)) {
      return { ok: false, error: `An objective with code "${trimmedCode}" already exists.` };
    }
    const id = newId('obj');
    set((state) => ({
      scenario: {
        ...state.scenario,
        objectives: { ...state.scenario.objectives, [id]: { ...decl, code: trimmedCode } },
      },
    }));
    return { ok: true, id };
  },

  updateObjective: (id, patch) => {
    set((state) => {
      const cur = state.scenario.objectives[id];
      if (!cur) return state;
      return {
        scenario: {
          ...state.scenario,
          objectives: { ...state.scenario.objectives, [id]: { ...cur, ...patch } },
        },
      };
    });
  },

  deleteObjective: (id) => {
    set((state) => {
      if (!state.scenario.objectives[id]) return state;
      const { [id]: _drop, ...rest } = state.scenario.objectives;
      // Cascade through every objectiveTags array.
      const scenarioWithoutObjective = { ...state.scenario, objectives: rest };
      const scenario = transformScenarioForObjectiveDelete(scenarioWithoutObjective, id);
      return { scenario };
    });
  },

  setPanelOpen: (open) => set({ panelOpen: open }),
  setPanelWidth: (width) =>
    set({ panelWidth: Math.max(PANEL_MIN, Math.min(PANEL_MAX, width)) }),

  setOpenModal: (kind) => set({ openModal: kind }),

  // ----- Published-version mutators -----

  publishDraft: (versionTag) => {
    const trimmed = (versionTag || '').trim();
    if (!trimmed) return { ok: false, error: 'Version tag required' };
    const existing = get().publishedVersions;
    if (existing.some((v) => v.versionTag === trimmed)) {
      return { ok: false, error: `A version tagged "${trimmed}" already exists. Pick a different tag.` };
    }
    // Errors block publish; warnings don't.
    const validation = validateScenario(get().scenario);
    if (validation.errors.length > 0) {
      return {
        ok: false,
        error: `Cannot publish: scenario has ${validation.errors.length} error${validation.errors.length === 1 ? '' : 's'}. Open Problems to fix them.`,
      };
    }
    // Deep clone the scenario so future edits don't mutate the published copy.
    // structuredClone is enough — no Date/Map/Set in the scenario shape.
    const snapshot: PublishedScenario = {
      scenario: structuredClone(get().scenario),
      publishedAt: new Date().toISOString(),
      versionTag: trimmed,
    };
    set((state) => ({
      publishedVersions: [snapshot, ...state.publishedVersions],
    }));
    return { ok: true, versionTag: trimmed };
  },

  restoreFromPublished: (versionTag) => {
    const version = get().publishedVersions.find((v) => v.versionTag === versionTag);
    if (!version) return false;
    // The restored draft is a deep clone — edits don't mutate the published copy.
    set({
      scenario: structuredClone(version.scenario),
      selectedId: version.scenario.startNodeId,
    });
    return true;
  },

  deletePublishedVersion: (versionTag) => {
    set((state) => ({
      publishedVersions: state.publishedVersions.filter((v) => v.versionTag !== versionTag),
    }));
  },

  // ----- Persistence mutators -----

  hydrate: async () => {
    if (get().hydrated || hydrationInFlight) return;
    hydrationInFlight = true;
    const repo = getRepository();
    const summaries = await repo.list();
    if (summaries.length === 0) {
      // Empty library — seed a clean blank scenario as the first one. (The
      // chest-pain demo lives at the runtime URL, not the editor.)
      const id = newId('scn');
      const now = new Date().toISOString();
      const record: StoredScenario = {
        id,
        draft: structuredClone(blankScenario),
        publishedVersions: [],
        createdAt: now,
        updatedAt: now,
      };
      await repo.save(record);
      set({
        scenario: record.draft,
        publishedVersions: [],
        scenarioId: id,
        selectedId: record.draft.startNodeId,
        createdAt: now,
        hydrated: true,
        saveStatus: 'saved',
      });
      return;
    }
    // Open the most-recently-updated scenario.
    const mostRecent = summaries[0];
    const record = await repo.load(mostRecent.id);
    if (record) {
      set({
        scenario: record.draft,
        publishedVersions: record.publishedVersions || [],
        scenarioId: record.id,
        selectedId: record.draft.startNodeId,
        createdAt: record.createdAt,
        hydrated: true,
        saveStatus: 'saved',
      });
    } else {
      // Listed but unloadable — degrade to hydrated with the default scenario.
      set({ hydrated: true, saveStatus: 'idle' });
    }
  },

  saveNow: async () => {
    const { scenarioId, scenario, publishedVersions, createdAt } = get();
    if (!scenarioId) return;
    set({ saveStatus: 'saving' });
    try {
      const repo = getRepository();
      await repo.save({
        id: scenarioId,
        draft: scenario,
        publishedVersions,
        createdAt,
        updatedAt: new Date().toISOString(),
      });
      set({ saveStatus: 'saved' });
    } catch {
      set({ saveStatus: 'error' });
    }
  },

  openScenario: async (id) => {
    const repo = getRepository();
    const record = await repo.load(id);
    if (!record) return;
    set({
      scenario: record.draft,
      publishedVersions: record.publishedVersions || [],
      scenarioId: record.id,
      selectedId: record.draft.startNodeId,
      createdAt: record.createdAt,
      saveStatus: 'saved',
      openModal: null,
    });
  },

  createScenario: async () => {
    const repo = getRepository();
    const id = newId('scn');
    const now = new Date().toISOString();
    // Fresh scenario seeded from the blank template.
    const draft = structuredClone(blankScenario);
    draft.title = 'New scenario';
    const record: StoredScenario = {
      id,
      draft,
      publishedVersions: [],
      createdAt: now,
      updatedAt: now,
    };
    await repo.save(record);
    set({
      scenario: draft,
      publishedVersions: [],
      scenarioId: id,
      selectedId: draft.startNodeId,
      createdAt: now,
      saveStatus: 'saved',
      openModal: null,
    });
  },

  duplicateScenario: async (id) => {
    const repo = getRepository();
    const source = await repo.load(id);
    if (!source) return;
    const newScenarioId = newId('scn');
    const now = new Date().toISOString();
    const draft = structuredClone(source.draft);
    draft.title = `${draft.title} (copy)`;
    const record: StoredScenario = {
      id: newScenarioId,
      draft,
      // A duplicate starts with no published versions — publishing is a
      // deliberate act on the new copy.
      publishedVersions: [],
      createdAt: now,
      updatedAt: now,
    };
    await repo.save(record);
    set({
      scenario: draft,
      publishedVersions: [],
      scenarioId: newScenarioId,
      selectedId: draft.startNodeId,
      createdAt: now,
      saveStatus: 'saved',
      openModal: null,
    });
  },

  deleteScenario: async (id) => {
    const repo = getRepository();
    await repo.remove(id);
    // If we deleted the open scenario, open another or seed a fresh one.
    if (get().scenarioId === id) {
      const summaries = await repo.list();
      if (summaries.length > 0) {
        await get().openScenario(summaries[0].id);
      } else {
        await get().createScenario();
      }
    }
  },

  setSaveStatus: (status) => set({ saveStatus: status }),

  getValidation: () => validateScenario(get().scenario),
}));
